/**
 * Whether a turn can become a rewind boundary. The server enforces this under
 * the session control lease, and the client shows `reason` as the disabled
 * tooltip of every rewind action, so this module stays pure: it imports types
 * and pure turn helpers only. `fork-policy.ts` builds on the same checks.
 */
import type { CodeSessionStatus } from '@/generated/prisma/enums';

import { isLiveTurnStatus } from './turn-utils';

export type RewindPolicyTurn = {
  id: string;
  status: string;
  /**
   * Whether OpenCode received the turn's prompt. The server knows only that
   * it was sent (`serverPolicyTurn`); the client also reads OpenCode's messages.
   */
  delivered: boolean;
  discardedAt?: Date | string | null;
};

export type RewindPolicySession = {
  status: CodeSessionStatus;
  /** In their order, first turn first. */
  turns: readonly RewindPolicyTurn[];
  /** A side agent's main session; null for a main session. */
  sideAgentParentId: string | null;
  /** A setup-only session (`isSetupOnlySession`), which neither rewinds nor forks. */
  setupOnly?: boolean;
  /**
   * A codebase scan finding's conversation (`isFindingFixSession`), whose first message carries
   * the finding for its agent: neither rewinds nor forks from that message.
   */
  findingConversation?: boolean;
  /** Only the client sees pending question forms; on the server they imply a live turn. */
  hasPendingForm?: boolean;
  /** A Git commit or push in flight; the server serializes those with rewinds under the lease. */
  gitActionPending?: boolean;
  /**
   * Only the server reads whether the session's side agents have work
   * (`hasSideAgentWork`); they edit its checkout in its VM.
   */
  sideAgentsWorking?: boolean;
};

/** `stopsTurn`: running or queued work is interrupted before the rewind is staged. */
export type RewindDecision = { ok: true; stopsTurn?: true } | { ok: false; reason: string };

export const REWIND_BLOCKED_REASON = {
  ARCHIVED: 'This session is archived',
  STARTING: 'Wait for the session to start',
  UNAVAILABLE: 'This session failed to start',
  GIT_ACTION: 'Wait for the Git action to finish',
  DISCARDED: 'This message was removed',
  UNSENT: 'This message never reached the agent',
  SIDE_AGENT: "Side chats share the main session's files; edit or fork from the main session",
  SIDE_AGENTS: 'Wait for the side chats to finish',
  SETUP_SESSION: 'A setup session can’t be edited or forked',
  FINDING_FIRST_MESSAGE:
    'The first message tells cubic about the finding; edit or fork from a later one',
} as const;

/** A saved turn as the server's checks see it: its prompt was sent once it has an inbox ID. */
export function serverPolicyTurn<T extends { inboxId: string | null }>(
  turn: T,
): T & Pick<RewindPolicyTurn, 'delivered'> {
  return { ...turn, delivered: turn.inboxId !== null };
}

function sessionBlockedReason(session: RewindPolicySession): string | undefined {
  // A rewind could drop the setup prompt, and a fork copies the session into a general one.
  if (session.setupOnly) {
    return REWIND_BLOCKED_REASON.SETUP_SESSION;
  }
  // OpenCode restores files across the checkout a side agent shares with its
  // main session, and a side agent's copied history holds the main agent's
  // file snapshots, so its rewind or fork would roll back the main agent's work.
  if (session.sideAgentParentId) {
    return REWIND_BLOCKED_REASON.SIDE_AGENT;
  }
  switch (session.status) {
    case 'archived':
      return REWIND_BLOCKED_REASON.ARCHIVED;
    case 'creating':
      return REWIND_BLOCKED_REASON.STARTING;
    case 'failed':
      return REWIND_BLOCKED_REASON.UNAVAILABLE;
    case 'idle':
    case 'running':
    case 'stopped':
      if (session.gitActionPending) {
        return REWIND_BLOCKED_REASON.GIT_ACTION;
      }
      return session.sideAgentsWorking ? REWIND_BLOCKED_REASON.SIDE_AGENTS : undefined;
    default: {
      const unhandled: never = session.status;
      return unhandled;
    }
  }
}

/**
 * The checks rewinding and forking share. The session is a main session and
 * active (a stopped sandbox is resumed or forked from its snapshot), no Git
 * action or side-agent work is in flight, and `turn`, when given, reached
 * OpenCode and was not discarded. `stopsTurn` marks running or queued work,
 * including a question waiting on the user.
 */
export function boundaryDecision(
  session: RewindPolicySession,
  turn: RewindPolicyTurn | undefined,
): RewindDecision {
  const blocked = (reason: string): RewindDecision => ({ ok: false, reason });
  const sessionReason = sessionBlockedReason(session);
  if (sessionReason !== undefined) {
    return blocked(sessionReason);
  }
  if (turn?.discardedAt) {
    return blocked(REWIND_BLOCKED_REASON.DISCARDED);
  }
  // A rewind or fork from it would leave the agent without the finding it is about.
  if (session.findingConversation && turn !== undefined && turn.id === session.turns[0]?.id) {
    return blocked(REWIND_BLOCKED_REASON.FINDING_FIRST_MESSAGE);
  }
  // A turn cancelled before delivery keeps an inbox ID for cleanup, so the
  // server counts it delivered, but OpenCode has no message for it: a rewind
  // is refused with MessageNotFoundError, which the server maps to this
  // reason, and a fork has nothing to copy.
  if (turn !== undefined && !turn.delivered) {
    return blocked(REWIND_BLOCKED_REASON.UNSENT);
  }
  if (
    session.status === 'running' ||
    session.hasPendingForm ||
    (turn !== undefined && isLiveTurnStatus(turn.status)) ||
    session.turns.some((candidate) => isLiveTurnStatus(candidate.status))
  ) {
    return { ok: true, stopsTurn: true };
  }
  return { ok: true };
}

/**
 * The shared checks, with running or queued work stopped first, as OpenCode's
 * halt() does. A rewind already waiting elsewhere is fine; staging again moves
 * its boundary.
 */
export function canRewindFromTurn(
  session: RewindPolicySession,
  turn: RewindPolicyTurn,
): RewindDecision {
  return boundaryDecision(session, turn);
}
